import {
  computeStatusLabel,
  STATUS_LABELS,
  STATUS_GROUPS
} from '../../constants/legislativeActions';

// Helper to parse date strings as local dates (fixes timezone issues)
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateString);
};

export const formatDate = (dateString) => {
  if (!dateString) return 'Unknown Date';
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

export const getDaysActive = (bill) => {
  if (!bill.history || bill.history.length === 0) return 0;
  const introducedDate = parseLocalDate(bill.history[bill.history.length - 1].date);
  if (!introducedDate) return 0;

  const currentStatus = computeStatusLabel(bill);
  const isFinal = STATUS_GROUPS.completed.includes(currentStatus) || STATUS_GROUPS.failed.includes(currentStatus);

  let endDate = new Date();
  if (isFinal && bill.history.length > 0) {
    endDate = parseLocalDate(bill.history[0].date) || new Date();
  }

  const diffTime = endDate - introducedDate;
  return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
};

export const getMostRecentActionDate = (bill) => {
  if (!bill.history || bill.history.length === 0) return bill.status_date || null;
  return bill.history[0].date;
};

// --- API COMPATIBILITY HELPERS ---

// Map unified status labels to legacy internal status keys for UI logic relevance
// (Status keys are used for badge color logic mapping in some components)
const mapLabelToInternalStatus = (label) => {
  switch (label) {
    case STATUS_LABELS.SIGNED_INTO_LAW: return 'law';
    case STATUS_LABELS.ADOPTED: return 'adopted';
    case STATUS_LABELS.VETOED: return 'vetoed';
    // Withdrawn is now merged into Dead, so we handle DEAD_FAILED (which is now 'Dead')
    case STATUS_LABELS.DEAD_FAILED: return 'failed';
    case STATUS_LABELS.GOVERNORS_DESK: return 'governorDesk'; // matches getStatusColor key
    case STATUS_LABELS.RESOLVING_DIFFERENCES: return 'reconciliation';
    case STATUS_LABELS.PASSED_ONE_CHAMBER: return 'secondChamberReview'; // Approximation
    case STATUS_LABELS.PASSED_BOTH_CHAMBERS: return 'governorDesk'; // Approximation
    case STATUS_LABELS.ON_THE_FLOOR: return 'chamberReview'; // Approximation
    case STATUS_LABELS.IN_COMMITTEE: return 'chamberReview'; // Approximation
    case STATUS_LABELS.INACTIVE_STALLED: return 'stalled';
    default: return 'introduced';
  }
};

export const getCurrentBillStatus = (bill) => {
  const label = computeStatusLabel(bill);
  return {
    status: mapLabelToInternalStatus(label),
    label: label
  };
};

export const getStatusColor = (status, isDarkMode) => {
  // Use mapLabelToInternalStatus keys if passed, or direct label matches
  const statusColors = {
    'introduced': isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800',
    'chamberReview': isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
    'secondChamberReview': isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
    'reconciliation': isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800',
    'governorDesk': isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800',
    'law': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
    'adopted': isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-800',
    'vetoed': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
    'withdrawn': isDarkMode ? 'bg-gray-900/50 text-gray-300' : 'bg-gray-100 text-gray-800',
    'failed': isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800',
    'stalled': isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800'
  };
  return statusColors[status] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
};

export const getBillStatus = (status) => {
  // Pass through label if it's already a clean string, otherwise map legacy keys
  const statusMap = {
    'chamberReview': 'House/Senate Review',
    'secondChamberReview': '2nd Chamber Review',
    'reconciliation': 'Reconciliation',
    'governorDesk': "Governor's Desk",
    'law': 'Signed into Law',
    'adopted': 'Adopted',
    'vetoed': 'Vetoed',
    'withdrawn': 'Withdrawn',
    'failed': 'Dead/Failed',
    'stalled': 'Stalled/Inactive',
  };
  return statusMap[status] || status;
};

// --- CARD STATUS SYSTEM (Refactored) ---

export const getBillCardData = (bill) => {
  if (!bill || !bill.history || bill.history.length === 0) {
    return { status: 'introduced', label: 'Introduced', date: bill.status_date };
  }

  const lastAction = bill.history[0];
  const label = computeStatusLabel(bill);

  // Return expected structure: status (internal key), label (display text), date
  return {
    status: mapLabelToInternalStatus(label),
    label: label,
    date: lastAction.date
  };
};

export const getBillCardBadge = (bill, primaryStatus) => {
  // Use computed label to determine badge
  const label = computeStatusLabel(bill);

  // No badges for terminal failure states - primary label covers them
  if (label === STATUS_LABELS.DEAD_FAILED ||
    label === STATUS_LABELS.VETOED ||
    label === STATUS_LABELS.WITHDRAWN ||
    label === STATUS_LABELS.INACTIVE_STALLED) {
    return null;
  }

  // Amber Badge: Stalled
  if (label === STATUS_LABELS.INACTIVE_STALLED) {
    // Distinguish between Inactive (>90 days) and Stalled
    // Calculate days since last action
    const lastDate = parseLocalDate(bill.history[0]?.date);
    if (lastDate) {
      const daysSince = (new Date() - lastDate) / (1000 * 60 * 60 * 24);
      if (daysSince > 90) return { label: 'Inactive', color: 'gray' };
    }
    return { label: 'Stalled', color: 'amber' };
  }

  return null;
};
